// src/modules/competitions/services/competitionService.ts

import { httpClient } from '../../../shared/services/httpClient';
import { API_ENDPOINTS } from '../../../shared/services/apiConfig';
import {
  CompetitionsListApiResponse,
  CompetitionDetailsApiResponse,
  RegistrationApiResponse,
  CategoryRegistrationApiResponse,
  RegistrationStatusApiResponse,
  MyRegistrationsApiResponse,
  TermsApiResponse,
  StatusApiResponse,
  ImageUploadApiResponse,
  CompetitionFilters,
  RegistrationData,
  CategoryRegistrationData,
  AcceptTermsData,
  ImageUploadData,
  TermsType,
  ApprovalStatus,
  EventInfoResponse,
  CompetitionDetails
} from '../types';
import { ApiResponse } from '../../../types/api';

class CompetitionService {


  /**
 * Obtener información completa del evento incluyendo imagen promocional
 */
  async getEventInfo(eventId: number): Promise<ApiResponse<any>> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_EVENT_INFO, {
      type: 'event',
      id: eventId.toString()
    });
  }

  /**
   * Obtener lista de competencias con filtros opcionales
   */
async getCompetitions(filters?: CompetitionFilters): Promise<ApiResponse<{
  competitions: CompetitionDetails[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}>> {
  const params: Record<string, string> = {};

  if (filters?.status) params.status = filters.status;
  if (filters?.category_id) params.category_id = filters.category_id.toString();
  if (filters?.limit) params.limit = filters.limit.toString();
  if (filters?.offset) params.offset = filters.offset.toString();

  return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_EVENT_LIST, params);
}

    /**
   * Obtener lista de competencias CON detalles (categorías incluidas)
   */
  async getCompetitionsWithDetails(filters?: CompetitionFilters): Promise<ApiResponse<{
    competitions: CompetitionDetails[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  }>> {
    const params: Record<string, string> = {};

    if (filters?.status) params.status = filters.status;
    if (filters?.category_id) params.category_id = filters.category_id.toString();
    if (filters?.limit) params.limit = filters.limit.toString();
    if (filters?.offset) params.offset = filters.offset.toString();

    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_EVENT_LIST, params);
  }

  /**
   * Obtener detalles completos de una competencia
   */
  async getCompetitionDetails(competitionId: number): Promise<ApiResponse<EventInfoResponse>> {
  return httpClient.get<EventInfoResponse>(API_ENDPOINTS.COMPETITIONS.GET_EVENT_INFO, {
    type: 'event',
    id: competitionId.toString()
  });
}

  /**
   * Registrarse en una competencia (nivel 1) - ACTUALIZADO
   */
  async registerInCompetition(data: RegistrationData): Promise<RegistrationApiResponse> {
    return httpClient.post<any>(API_ENDPOINTS.COMPETITIONS.REGISTER, data);
  }

  /**
   * Obtener estado de mi registro en una competencia - NUEVO
   */
  async getRegistrationStatus(eventId: number): Promise<RegistrationStatusApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.REGISTRATION_STATUS, {
      event_id: eventId.toString()
    });
  }

  /**
   * Inscribirse en una categoría (nivel 2) - NUEVO
   */
  async registerInCategory(data: CategoryRegistrationData): Promise<CategoryRegistrationApiResponse> {
    return httpClient.post<any>(API_ENDPOINTS.COMPETITIONS.REGISTER_CATEGORY, data);
  }

  /**
   * Obtener mis registros en competencias - NUEVO
   */
  async getMyRegistrations(): Promise<MyRegistrationsApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.MY_REGISTRATIONS);
  }

  /**
   * Obtener términos y condiciones de una competencia
   */
  async getTermsAndConditions(
    competitionId: number,
    type: TermsType = 'participant'
  ): Promise<TermsApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.TERMS, {
      event_id: competitionId.toString(), // NOTA: cambié competition_id a event_id para coincidir con backend
      type
    });
  }

  /**
   * Aceptar términos y condiciones
   */
  async acceptTerms(data: AcceptTermsData): Promise<ApiResponse<null>> {
    return httpClient.post<null>(API_ENDPOINTS.COMPETITIONS.TERMS, data);
  }

  /**
  * Obtener estado del participante en una competencia
  * ✅ ACTUALIZADO: Ahora acepta participant_id directamente (más confiable)
  */
  async getParticipantStatus(
    competitionId?: number,
    participantId?: number
  ): Promise<StatusApiResponse> {
    const params: Record<string, string> = {};

    // ✅ Priorizar participant_id si existe
    if (participantId) {
      params.participant_id = participantId.toString();
    } else if (competitionId) {
      params.competition_id = competitionId.toString();
    } else {
      return {
        success: false,
        message: 'Se requiere participant_id o competition_id',
        data: null
      };
    }

    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.STATUS, params);
  }

  /**
   * Subir imagen para una competencia
   */
  async uploadImage(data: ImageUploadData): Promise<ImageUploadApiResponse> {
    const formData = new FormData();
    formData.append('participant_id', data.participation_id.toString());
    formData.append('image_type_id', data.image_type_id.toString());
    formData.append('image', data.image);

    return httpClient.postFormData<any>(API_ENDPOINTS.COMPETITIONS.SUBMIT_IMAGE, formData);
  }

  /**
   * Calcular progreso de un participante
   */
  calculateProgress(uploadedImages: number, requiredImages: number): {
    percentage: number;
    completed: boolean;
    remaining: number;
  } {
    const percentage = requiredImages > 0 ? (uploadedImages / requiredImages) * 100 : 0;

    return {
      percentage: Math.round(percentage * 100) / 100,
      completed: uploadedImages >= requiredImages,
      remaining: Math.max(0, requiredImages - uploadedImages)
    };
  }

  /**
   * Validar archivo antes de subir
   */
  validateImageFile(file: File, allowedFormats: string[], maxSizeMB: number): {
    isValid: boolean;
    error?: string;
  } {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `El archivo es muy grande. Tamaño máximo: ${maxSizeMB}MB`
      };
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedFormats.includes(fileExtension)) {
      return {
        isValid: false,
        error: `Formato no permitido. Formatos aceptados: ${allowedFormats.join(', ')}`
      };
    }

    // Validar tipo MIME
    const validMimeTypes: string[] = [];
    allowedFormats.forEach(format => {
      switch (format) {
        case 'jpg':
        case 'jpeg':
          validMimeTypes.push('image/jpeg');
          break;
        case 'png':
          validMimeTypes.push('image/png');
          break;
        case 'gif':
          validMimeTypes.push('image/gif');
          break;
        case 'webp':
          validMimeTypes.push('image/webp');
          break;
      }
    });

    if (validMimeTypes.length > 0 && !validMimeTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Tipo de archivo no válido'
      };
    }

    return { isValid: true };
  }

  /**
   * Formatear fecha para mostrar
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Verificar si una competencia está abierta para registro
   */
  isRegistrationOpen(competition: any): {
    isOpen: boolean;
    reason?: string;
  } {
    const now = new Date();
    const registrationEnd = competition.registration_end ? new Date(competition.registration_end) : null;
    const startDate = new Date(competition.start_date);

    if (competition.status === 'cancelled') {
      return { isOpen: false, reason: 'La competencia ha sido cancelada' };
    }

    if (competition.status === 'completed') {
      return { isOpen: false, reason: 'La competencia ya finalizó' };
    }

    if (competition.status === 'active') {
      return { isOpen: false, reason: 'La competencia ya está en curso' };
    }

    if (registrationEnd && now > registrationEnd) {
      return { isOpen: false, reason: 'El período de registro ha terminado' };
    }

    if (now > startDate) {
      return { isOpen: false, reason: 'La competencia ya comenzó' };
    }

    if (competition.max_participants && competition.current_participants >= competition.max_participants) {
      return { isOpen: false, reason: 'La competencia está llena' };
    }

    return { isOpen: true };
  }

  /**
   * Obtener texto del estado de envío
   */
  getSubmissionStatusText(status: string): string {
    switch (status) {
      case 'not_started':
        return 'No iniciado';
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completado';
      case 'disqualified':
        return 'Descalificado';
      default:
        return 'Desconocido';
    }
  }

  /**
   * Obtener texto del estado de pago
   */
  getPaymentStatusText(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'paid':
        return 'Pagado';
      case 'partial':
        return 'Pago parcial';
      case 'refunded':
        return 'Reembolsado';
      default:
        return 'Desconocido';
    }
  }

  /**
   * Obtener texto del estado de aprobación - NUEVO
   */
  getApprovalStatusText(status: ApprovalStatus): string {
    switch (status) {
      case 'pending':
        return 'Pendiente de aprobación';
      case 'approved':
        return 'Aprobado';
      case 'rejected':
        return 'Rechazado';
      default:
        return 'Desconocido';
    }
  }

  /**
   * Obtener color del estado
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'upcoming':
      case 'pending':
      case 'not_started':
        return '#f39c12';
      case 'active':
      case 'in_progress':
        return '#3498db';
      case 'completed':
      case 'paid':
      case 'approved':
        return '#27ae60';
      case 'cancelled':
      case 'disqualified':
      case 'refunded':
      case 'rejected':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  }

  async checkParticipant(eventId: number): Promise<ApiResponse<{ is_participant: boolean }>> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.CHECK_PARTICIPANT, {
      event_id: eventId.toString()
    });
  }

  /**
   * Obtener color del estado de aprobación - NUEVO
   */
  getApprovalStatusColor(status: ApprovalStatus): string {
    return this.getStatusColor(status);
  }
}

export const competitionService = new CompetitionService();